/**
 * availability-refresh.js
 *
 * Product cards are rendered server-side and served from Shopify's full-page
 * cache, so a card can keep showing a "sold out" badge after the item is back
 * in stock, until that cache refreshes (minutes to hours). For each card that
 * was rendered sold out, this confirms availability against the live product
 * JSON (`/products/{handle}.js`) and removes the badge if the item is actually
 * available — the same fetch-on-demand pattern the theme already uses for the
 * cart, facets and product-page variant updates.
 *
 * Only sold-out cards opt in (`data-soldout-card` + `data-product-url`), so
 * in-stock cards are untouched and make no requests. Results are cached in
 * localStorage for 5 minutes to avoid re-pinging across page views, and a
 * cached "available" result clears the badge instantly (before the card is
 * even scrolled to) so the stale badge is never seen.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window) || !('fetch' in window)) return;

  var SELECTOR = '[data-soldout-card][data-product-url]';
  var CACHE_KEY = 'availability-refresh';
  var CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  // Verify ~a screen before a card scrolls into view, so the badge is cleared
  // before it's ever visible (no flash of a stale "sold out" on fast scroll).
  // Percentages are relative to the viewport, so the lead scales with its height.
  var ROOT_MARGIN = '30% 0% 100% 0%';

  var inflight = new Map(); // url -> Promise<boolean|null>; dedupes same-page repeats
  var processed = new WeakSet();

  function readCache() {
    try {
      return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function getCached(url) {
    var entry = readCache()[url];
    return entry && Date.now() - entry.ts < CACHE_TTL ? entry.available : undefined;
  }

  function setCached(url, available) {
    try {
      var now = Date.now();
      var cache = readCache();
      cache[url] = { available: available, ts: now };
      Object.keys(cache).forEach(function (key) {
        if (now - cache[key].ts >= CACHE_TTL) delete cache[key]; // prune expired
      });
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      /* storage unavailable (private mode / quota) — caching is best-effort */
    }
  }

  function hideBadge(card) {
    card.querySelectorAll('[data-soldout-indicator]').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  function fetchAvailable(url) {
    if (!inflight.has(url)) {
      inflight.set(
        url,
        fetch(url + '.js', { headers: { Accept: 'application/json' } })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.json();
          })
          .then(function (product) {
            var available = product.available === true;
            setCached(url, available);
            return available;
          })
          .catch(function () {
            inflight.delete(url); // allow a retry next time the card is seen
            return null; // unknown -> keep the server-rendered sold-out state
          })
      );
    }
    return inflight.get(url);
  }

  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        io.unobserve(card);
        fetchAvailable(card.getAttribute('data-product-url')).then(function (available) {
          if (available === true) hideBadge(card);
        });
      });
    },
    { rootMargin: ROOT_MARGIN }
  );

  function process(card) {
    var url = card.getAttribute('data-product-url');
    if (!url) return;
    var cached = getCached(url);
    if (cached === true) hideBadge(card); // known available -> clear now, no request
    else if (cached === undefined) io.observe(card); // unknown -> verify as it approaches
    // cached === false -> confirmed sold out within TTL, nothing to do
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(function (card) {
      if (processed.has(card)) return;
      processed.add(card);
      process(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  // Grids injected later (facet filtering, pagination, section reloads, ...).
  var scheduled = false;
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length && !scheduled) {
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          scan();
        });
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
